import Vue from 'vue';
import RecaptchaDialog from '@/components/RecaptchaDialog.vue';
import store from '@/store';
import router from '@/core/router';

// Global lock to prevent multiple dialogs from opening simultaneously
let currentDialogPromise = null;

/**
 * Show checkbox reCAPTCHA dialog with action
 * @param {string} action - The reCAPTCHA action
 * @returns {Promise} Promise that resolves with reCAPTCHA result or rejects on error/cancel
 */
export function showCheckboxRecaptchaDialog(action = '') {
  // If a dialog is already open, wait for it to complete and reuse the result
  if (currentDialogPromise) {
    return currentDialogPromise;
  }

  const dialogPromise = new Promise((resolve, reject) => {
    // Create a temporary container for the dialog
    const container = document.createElement('div');
    document.body.appendChild(container);

    // Create Vue instance for the dialog
    const DialogConstructor = Vue.extend(RecaptchaDialog);
    const dialogInstance = new DialogConstructor({
      store, // Inject the Vuex store
      router, // Inject the router
      propsData: {
        visible: true,
        action: action,
      },
    });

    // Handle resolve event
    dialogInstance.$on('resolve', (result) => {
      // Set visible to false to trigger cleanup
      dialogInstance.visible = false;
      // Wait for next tick to ensure cleanup is done
      Vue.nextTick(() => {
        cleanup();
        currentDialogPromise = null; // Clear the global lock
        resolve(result);
      });
    });

    // Handle reject event
    dialogInstance.$on('reject', (error) => {
      // Set visible to false to trigger cleanup
      dialogInstance.visible = false;
      // Wait for next tick to ensure cleanup is done
      Vue.nextTick(() => {
        cleanup();
        currentDialogPromise = null; // Clear the global lock
        reject(error);
      });
    });

    // Handle visibility update
    dialogInstance.$on('update:visible', (newVal) => {
      dialogInstance.visible = newVal;
    });

    // Cleanup function
    const cleanup = () => {
      if (dialogInstance) {
        dialogInstance.$destroy();
      }
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };

    // Mount the dialog
    dialogInstance.$mount(container);
  });

  // Store the promise globally to prevent multiple dialogs
  currentDialogPromise = dialogPromise;
  return dialogPromise;
}

export default {
  showCheckboxRecaptchaDialog,
};
